"use client"

import { useEffect } from "react"

import { getPrimaryTenantRole } from "@/lib/user-utils"
import { isLocalDevelopment } from "@/lib/utils"
import { type CurrentUser } from "@/services/users/user.types"

/**
 * LogRocket SDK initialization component
 * Initializes LogRocket for session recording and user identification
 * Only initializes in the demo environment
 *
 * Based on LogRocket documentation:
 * - https://docs.logrocket.com/reference/init
 * - https://docs.logrocket.com/reference/identify
 */

export const setLogRocketUser = (user: CurrentUser) => {
  if (!window.settings.LOGROCKET_APP_ID) return
  if (window.settings.ENV_NAME !== "demo") return // TODO: Remove this after testing

  // Use user UUID as the identifier and include additional user traits
  const fullName =
    user.profile.first_name && user.profile.last_name
      ? `${user.profile.first_name} ${user.profile.last_name}`.trim()
      : user.display_name

  const primaryRole = getPrimaryTenantRole(user)

  // no-dd-sa:typescript-best-practices/no-var-requires
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
  const LogRocket = require("logrocket")

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  LogRocket.identify(user.uuid, {
    name: fullName,
    tenant_uuid: user.tenant_uuid,
    account_id: user.account_id || "unknown",
    // Add other useful traits for debugging and filtering sessions
    user_type: primaryRole,
    display_name: user.display_name,
    status: user.status,
    license_type: user.license_type,
    created_at: user.created_at,
  })
}

export function LogRocketInit() {
  useEffect(() => {
    // Only initialize LogRocket in the demo environment
    if (window.settings.ENV_NAME !== "demo") {
      // TODO: Remove this after testing
      return
    }

    // Check if LogRocket App ID is available
    if (!window.settings.LOGROCKET_APP_ID) {
      if (isLocalDevelopment()) {
        // no-dd-sa:typescript-best-practices/no-console
        // eslint-disable-next-line no-console -- Important warning for missing configuration
        console.warn(
          "LogRocket SDK: Skipping LogRocket initialization in local development with missing LOGROCKET_APP_ID. " +
            "This is expected for local development unless you're specifically testing LogRocket."
        )
      } else {
        // no-dd-sa:typescript-best-practices/no-console
        // eslint-disable-next-line no-console -- Important warning for missing configuration
        console.warn(
          "LogRocket SDK: Missing LOGROCKET_APP_ID for demo environment"
        )
      }
      return
    }
    // no-dd-sa:typescript-best-practices/no-var-requires
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
    const LogRocket = require("logrocket")

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    LogRocket.init(window.settings.LOGROCKET_APP_ID)
  }, []) // Empty dependency array means this runs once on mount

  // Render nothing - this component is only included so that the init code above will run client-side
  return null
}
